"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { i18n, Locale } from "@/i18n-config";
import { clsx } from "@/lib/utils";

export function LanguageSwitcher({ currentLang }: { currentLang: Locale }) {
  const pathname = usePathname();
  const languageNames: Record<string, string> = {
    en: "English",
    es: "Español",
    pl: "Polski",
  };

  const redirectedPathName = (locale: Locale) => {
    if (!pathname) return "/";
    const segments = pathname.split("/");
    const hasLocale = i18n.locales.includes(segments[1] as Locale);

    if (hasLocale) {
      if (locale === i18n.defaultLocale) {
        segments.splice(1, 1);
        const newPath = segments.join("/");
        return newPath || "/";
      } else {
        segments[1] = locale;
        return segments.join("/");
      }
    } else {
      if (locale === i18n.defaultLocale) {
        return pathname;
      } else {
        return `/${locale}${pathname === "/" ? "" : pathname}`;
      }
    }
  };

  const dropdownClasses = clsx([
    "py-1",
    "[&_a]:block [&_a]:w-full [&_a]:px-4 [&_a]:py-2 [&_a]:text-left [&_a]:text-sm",
    "[&_a[aria-current='page']]:bg-muted [&_a[aria-current='page']]:text-foreground [&_a[aria-current='page']]:font-semibold",
    "[&_a:not([aria-current='page'])]:text-muted-foreground",
    "hover:[&_a:not([aria-current='page'])]:bg-muted hover:[&_a:not([aria-current='page'])]:text-foreground",
  ]);

  return (
    <details className="relative inline-block text-left group/details">
      <summary className="inline-flex cursor-pointer list-none items-center justify-center gap-x-1.5 rounded-md text-sm font-medium leading-6 text-foreground hover:text-muted-foreground transition-colors [&::-webkit-details-marker]:hidden">
        {languageNames[currentLang].toUpperCase().substring(0, 2)}
        <svg
          className="-mr-1 h-4 w-4 text-muted-foreground group-open/details:rotate-180 transition-transform"
          viewBox="0 0 20 20"
          fill="currentColor"
          aria-hidden="true"
        >
          <path
            fillRule="evenodd"
            d="M5.22 8.22a.75.75 0 0 1 1.06 0L10 11.94l3.72-3.72a.75.75 0 1 1 1.06 1.06l-4.25 4.25a.75.75 0 0 1-1.06 0L5.22 9.28a.75.75 0 0 1 0-1.06Z"
            clipRule="evenodd"
          />
        </svg>
      </summary>

      <div
        className="absolute right-0 z-10 mt-2 w-32 origin-top-right rounded-md bg-background border border-border shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none"
        role="menu"
      >
        <div className={dropdownClasses} role="none">
          {i18n.locales.map((locale) => (
            <Link
              key={locale}
              href={redirectedPathName(locale)}
              aria-current={locale === currentLang ? "page" : undefined}
              role="menuitem"
            >
              {languageNames[locale]}
            </Link>
          ))}
        </div>
      </div>
    </details>
  );
}
